import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { all_routes } from '../../../../router/all_routes';
import CommonSelect from '../../../../../core/common/commonSelect';
import { Button } from 'react-bootstrap';
import { toast } from 'react-toastify';
import CollapseHeader from '../../../../../core/common/collapse-header/collapse-header';
import {
  getAllComapniesList,
  getCompanySettings,
  saveCompanySettings
} from '../../../../../services/attendanceService';

type Company = {
  id: number;
  name: string;
  type: string;
};

type CompanySettings = {
  id?: number;
  companyId: string;
  companyType: string;
  settings: {
    biometricEnabled: boolean;
  };
};

const DEFAULT_SETTINGS: CompanySettings = {
  companyId: '',
  companyType: '',
  settings: {
    biometricEnabled: false
  }
};

const CompanySettingsPage = () => {
  // State for companies and settings
  const [companies, setCompanies] = useState<Array<{ value: string, label: string, type: string }>>([]);
  const [selectedCompany, setSelectedCompany] = useState<any | null>(null);
  const [companySettings, setCompanySettings] = useState<CompanySettings>(DEFAULT_SETTINGS);
  const [isLoading, setIsLoading] = useState({
    companies: false,
    settings: false
  });
  const [isProcessing, setIsProcessing] = useState(false);

  // Fetch companies on component mount
  useEffect(() => {
    const fetchCompanies = async () => {
      setIsLoading(prev => ({ ...prev, companies: true }));
      try {
        const res = await getAllComapniesList();
        const mappedCompanies = res.data.data.data.map((company: Company) => ({
          value: company.id.toString(),
          label: company.name,
          type: company.type.toLowerCase()
        }));
        setCompanies(mappedCompanies);

        // Automatically select the first company if available
        if (mappedCompanies.length > 0) {
          setSelectedCompany(mappedCompanies[0]);
        }
      } catch (error) {
        console.error('Error fetching companies:', error);
        toast.error('Failed to load companies');
      } finally {
        setIsLoading(prev => ({ ...prev, companies: false }));
      }
    };

    fetchCompanies();
  }, []);

  // Fetch settings when company changes
  useEffect(() => {
    const fetchSettings = async (companyId: string, companyType: string) => {
      if (!companyId || !companyType) return;

      setIsLoading(prev => ({ ...prev, settings: true }));
      try {
        const res = await getCompanySettings(companyId, companyType);

        if (res.data.data?.data) {
          // Match the real API response structure
          setCompanySettings({
            id: res.data.data.data.id,
            companyId: res.data.data.data.companyId,
            companyType: res.data.data.data.companyType,
            settings: {
              biometricEnabled: res.data.data.data.settings.biometricEnabled
            }
          });
        } else {
          // Initialize with defaults if no settings exist
          setCompanySettings({
            ...DEFAULT_SETTINGS,
            companyId,
            companyType
          });
        }
      } catch (error) {
        console.error('Error fetching settings:', error);
        toast.error('Failed to load company settings');
        setCompanySettings({
          ...DEFAULT_SETTINGS,
          companyId,
          companyType
        });
      } finally {
        setIsLoading(prev => ({ ...prev, settings: false }));
      }
    };

    if (selectedCompany && selectedCompany.value && selectedCompany.type) {
      fetchSettings(selectedCompany.value, selectedCompany.type);
    } else {
      setCompanySettings(DEFAULT_SETTINGS);
    }
  }, [selectedCompany]);

  const handleCompanyChange = (selectedOption: any) => {
    setSelectedCompany(selectedOption);
  };

  const toggleBiometricSetting = async () => {
    if (!selectedCompany || !companySettings) return;

    setIsProcessing(true);
    try {
      const currentValue = companySettings.settings.biometricEnabled;
      const updatedSettings = {
        ...companySettings,
        settings: {
          biometricEnabled: !currentValue
        }
      };

      console.log({
        companyId: selectedCompany.value,
        companyType: selectedCompany.type,
        settings: updatedSettings.settings
      })

      const res = await saveCompanySettings({
        companyId: selectedCompany.value,
        companyType: selectedCompany.type,
        settings: updatedSettings.settings
      });

      // Update state with the response data to ensure UI matches server state
      setCompanySettings({
        id: res.data.data.id,
        companyId: res.data.data.companyId,
        companyType: res.data.data.companyType,
        settings: {
          biometricEnabled: res.data.data.data.settings.biometricEnabled
        }
      });

      toast.success(res.data.message || 'Settings saved successfully');
    } catch (error: any) {
      console.error('Error saving settings:', error);
      toast.error(error.response?.data?.message || 'Failed to save settings');

      // Re-fetch settings to ensure UI matches server state
      if (selectedCompany?.value && selectedCompany?.type) {
        const res = await getCompanySettings(selectedCompany.value, selectedCompany.type);
        if (res.data.data?.data) {
          setCompanySettings({
            id: res.data.data.data.id,
            companyId: res.data.data.data.companyId,
            companyType: res.data.data.data.companyType,
            settings: {
              biometricEnabled: res.data.data.data.settings.biometricEnabled
            }
          });
        }
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const isLoadingAny = isLoading.companies || isLoading.settings;

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Breadcrumb */}
        <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
          <div className="my-auto mb-2">
            <h2 className="mb-1">Attendance Settings</h2>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={all_routes.adminDashboard}>
                    <i className="ti ti-smart-home" />
                  </Link>
                </li>
                <li className="breadcrumb-item">Company</li>
                <li className="breadcrumb-item active" aria-current="page">
                  Settings
                </li>
              </ol>
            </nav>
          </div>
          <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
            <div className="me-3">
              <CommonSelect
                options={companies}
                defaultValue={selectedCompany}
                onChange={handleCompanyChange}
                className="select-filter"
                isDisabled={isProcessing || isLoading.companies}
              />
            </div>
            <div className="head-icons ms-2">
              <CollapseHeader />
            </div>
          </div>
        </div>

        {/* Settings Panel */}
        {isLoadingAny ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2">
              {isLoading.companies ? 'Loading companies...' : 'Loading settings...'}
            </p>
          </div>
        ) : (
          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <h5>Attendance Settings</h5>
                </div>
                <div className="card-body">
                  {selectedCompany ? (
                    <>
                      <div className="mb-4">
                        <h6 className="mb-3">Company: {selectedCompany.label}</h6>
                        <p className="text-muted">
                          Type: {selectedCompany.type.charAt(0).toUpperCase() + selectedCompany.type.slice(1)}
                        </p>
                        {!companySettings.id && (
                          <div className="alert alert-info mt-2">
                            <i className="ti ti-info-circle me-2"></i>
                            Using default settings for this company
                          </div>
                        )}
                      </div>

                      <div className="form-group">
                        <div className="form-check form-switch">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="biometricToggle"
                            checked={companySettings.settings.biometricEnabled}
                            onChange={toggleBiometricSetting}
                            disabled={isProcessing}
                          />
                          <label className="form-check-label" htmlFor="biometricToggle">
                            Enable Biometric Authentication
                          </label>
                        </div>
                        <small className="text-muted">
                          When enabled, employees will be required to use biometric authentication
                          (fingerprint or face recognition) to clock in/out.
                        </small>
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-5">
                      <div className="empty-state">
                        <i className="ti ti-settings-off fs-6 text-muted mb-3"></i>
                        <h5>No companies available</h5>
                        <p className="text-muted">
                          There are no companies to display settings for
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CompanySettingsPage;